import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store';
import { fetchAgents } from '@/store/slices/agentSlice';
import AgentGrid from '@/components/agent/AgentGrid/AgentGrid';
import CTASection from '@/components/home/CTASection';
import { AgentSearchFilters } from '@/components/agent/AgentSearchFilters/AgentSearchFilters';

// Icon Components
const HomeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 6L9 17L4 12" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DollarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2V22M17 8C17 10.2091 14.2091 12 11 12C7.79086 12 5 10.2091 5 8C5 5.79086 7.79086 4 11 4C14.2091 4 17 5.79086 17 8ZM17 16C17 18.2091 14.2091 20 11 20C7.79086 20 5 18.2091 5 16C5 13.7909 7.79086 12 11 12C14.2091 12 17 13.7909 17 16Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const StarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BuildingIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 21H21M4 21V7L12 3L20 7V21M9 9V13M15 9V13M9 17V21M15 17V21" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CastleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 21H19M5 21V11H19V21M5 21H3M19 21H21M9 11V7C9 6.44772 9.44772 6 10 6H14C14.5523 6 15 6.44772 15 7V11M9 11H15M9 11H7M15 11H17M3 11H21M7 3L7 7M17 3L17 7" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const FactoryIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 21H21M3 21V11L7 8V21M3 21H7M7 21V8L11 5V21M7 21H11M11 21V5L15 8V21M11 21H15M15 21V8L19 11V21M15 21H19M19 21H21" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Agents: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { agents, isLoading, error, pagination } = useSelector(
    (state: RootState) => state.agents
  );

  useEffect(() => {
    // Fetch agents when component mounts
    dispatch(fetchAgents({
      page: 1,
      limit: 12,
      sortBy: 'rating',
      sortOrder: 'desc',
    }));
  }, [dispatch]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Search */}
      <div className="flex flex-col items-center w-full relative bg-white">
        <div className="w-full bg-white py-16 md:py-20 lg:py-24 px-6 md:px-12 lg:px-16 xl:px-20">
          <div className="max-w-6xl mx-auto flex flex-col gap-2">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold leading-[150%] text-primary">
              Find Your Expert Agent
            </h1>
            <p className="text-sm md:text-base font-normal leading-[150%] text-black">
              Meet our expert real estate agents who are dedicated to helping you find your dream home. With years of experience and local market knowledge, they're here to guide you every step of the way.
            </p>
          </div>
        </div>

        {/* Search Filters Section */}
        <AgentSearchFilters />
      </div>

      {/* Featured Agents Section */}
      <div className="w-full bg-white py-16 lg:border-t md:py-20 lg:py-24 px-6 md:px-12 lg:px-16 xl:px-20 -mt-12 md:-mt-16 lg:-mt-20 relative z-0">
        <div className="max-w-6xl mx-auto flex flex-col gap-8 md:gap-12 pt-12 md:pt-16 lg:pt-20">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row items-start lg:items-end gap-4 lg:gap-16">
            <div className="flex-1 flex flex-col gap-2">
              <div className="relative">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold leading-[150%] text-primary">
                  Featured Agents
                </h1>
              </div>
              <p className="text-sm md:text-base font-normal leading-[150%] text-black">
                Explore our handpicked selection of featured agents. Each agent offers exceptional expertise and local market knowledge to help you find your dream property. Click "View Details" for more information.
              </p>
            </div>
            
            <button className="px-4 py-2.5 bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90 transition-colors whitespace-nowrap">
              <span className="text-sm font-medium leading-[150%] text-white">
                View All Agents
              </span>
            </button>
          </div>
          
          {/* Agents Grid */}
          <div className="flex flex-col gap-6">
            <AgentGrid
              agents={agents}
              isLoading={isLoading}
              error={error}
              emptyMessage="No agents available at the moment. Please check back later."
            />
            
            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between pt-3 border-t border-gray-300">
                <div className="text-sm md:text-base font-medium leading-[150%]">
                  <span className="text-primary">Page {pagination.page}</span>
                  <span className="text-primary"> of {pagination.totalPages}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    className="p-1.5 md:p-2 border border-gray-300 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={pagination.page === 1}
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M25.5 15C25.5 15.6213 24.9963 16.125 24.375 16.125L8.41812 16.125L14.6547 22.0641C15.1026 22.4947 15.1166 23.2069 14.6859 23.6548C14.2553 24.1026 13.5431 24.1166 13.0953 23.6859L4.84525 15.8109C4.62466 15.5988 4.5 15.306 4.5 15C4.5 14.694 4.62466 14.4012 4.84525 14.1891L13.0953 6.31406C13.5431 5.88342 14.2553 5.89739 14.6859 6.34525C15.1166 6.79312 15.1026 7.5053 14.6547 7.93594L8.41812 13.875L24.375 13.875C24.9963 13.875 25.5 14.3787 25.5 15Z" fill="#666666"/>
                    </svg>
                  </button>
                  <button 
                    className="p-1.5 md:p-2 border border-gray-300 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={pagination.page === pagination.totalPages}
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M4.5 15C4.5 14.3787 5.00368 13.875 5.625 13.875L21.5819 13.875L15.3453 7.93593C14.8974 7.50529 14.8834 6.79312 15.3141 6.34525C15.7447 5.89738 16.4569 5.88342 16.9047 6.31406L25.1547 14.1891C25.3753 14.4012 25.5 14.694 25.5 15C25.5 15.306 25.3753 15.5988 25.1547 15.8109L16.9047 23.6859C16.4569 24.1166 15.7447 24.1026 15.3141 23.6547C14.8834 23.2069 14.8974 22.4947 15.3453 22.0641L21.5819 16.125L5.625 16.125C5.00368 16.125 4.5 15.6213 4.5 15Z" fill="#333333"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Agent Portfolio Section */}
      <div className="w-full bg-white py-16 md:py-20 lg:py-24 px-6 md:px-12 lg:px-16 xl:px-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col gap-8 md:gap-12">
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold leading-[150%] text-primary">Our Expert Team</h2>
              <p className="text-sm md:text-base font-normal leading-[150%] text-black">
                Licensed professionals with proven track records and deep local market expertise
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5">
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-tertiary">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <HomeIcon />
                </div>
                <div className="text-xl md:text-2xl font-semibold text-secondary mb-1">{pagination.total || 0}</div>
                <div className="text-xs md:text-sm font-medium text-secondary">Expert Agents</div>
                <div className="text-xs text-secondary mt-1">Licensed professionals</div>
              </div>
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-tertiary">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <CheckIcon />
                </div>
                <div className="text-xl md:text-2xl font-semibold text-secondary mb-1">15+</div>
                <div className="text-xs md:text-sm font-medium text-secondary">Years Experience</div>
                <div className="text-xs text-secondary mt-1">Average per agent</div>
              </div>
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-tertiary">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <DollarIcon />
                </div>
                <div className="text-xl md:text-2xl font-semibold text-secondary mb-1">500+</div>
                <div className="text-xs md:text-sm font-medium text-secondary">Happy Clients</div>
                <div className="text-xs text-secondary mt-1">Satisfied customers</div>
              </div>
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-tertiary">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <StarIcon />
                </div>
                <div className="text-xl md:text-2xl font-semibold text-secondary mb-1">4.9★</div>
                <div className="text-xs md:text-sm font-medium text-secondary">Average Rating</div>
                <div className="text-xs text-secondary mt-1">Based on reviews</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Find Your Perfect Agent Section */}
      <div className="w-full bg-white py-16 md:py-20 lg:py-24 px-6 md:px-12 lg:px-16 xl:px-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col gap-8 md:gap-12">
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold leading-[150%] text-primary">
                Our Expertise
              </h2>
              <p className="text-sm md:text-base font-normal leading-[150%] text-black">
                Our agents specialize in different areas of real estate to provide you with expert guidance
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-tertiary cursor-pointer hover:opacity-90 transition-opacity">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <HomeIcon />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-secondary mb-2">Residential Sales</h3>
                <p className="text-xs md:text-sm font-normal text-secondary leading-[150%]">Buying and selling homes, condos, and townhouses with personalized service and local market expertise</p>
              </div>
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-tertiary cursor-pointer hover:opacity-90 transition-opacity">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <BuildingIcon />
                </div>
                <h3 className="text-base md:text-md font-semibold text-secondary mb-2">Commercial Real Estate</h3>
                <p className="text-xs md:text-sm font-normal text-secondary leading-[150%]">Office spaces, retail properties, and investment opportunities with comprehensive market analysis</p>
              </div>
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-tertiary cursor-pointer hover:opacity-90 transition-opacity">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <CastleIcon />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-secondary mb-2">Luxury Properties</h3>
                <p className="text-xs md:text-sm font-normal text-secondary leading-[150%]">High-end estates and premium properties with exclusive access and discreet service</p>
              </div>
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-tertiary cursor-pointer hover:opacity-90 transition-opacity">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <FactoryIcon />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-secondary mb-2">Local Expertise</h3>
                <p className="text-xs md:text-sm font-normal text-secondary leading-[150%]">Deep knowledge of local markets, neighborhoods, and property values</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

export default Agents;

